import type { TableCustomizations } from '@/types/report';
import type { TableCellColor } from '@/types/report-payload';

/**
 * Cor automática da coluna "Classificação" das tabelas de instrumento.
 *
 * O dicionário cobre APENAS a família de posição na norma, onde alto é sempre
 * melhor. Alto/Baixo, Clínico, risco e severidade ficam de fora de propósito:
 * numa escala de sintoma alto é pior, e a direção não dá para inferir do rótulo
 * sozinho — pintar "Muito Alto" de verde numa SCARED diria que muita ansiedade
 * é bom. Rótulo desconhecido não recebe cor; a cor manual da célula
 * (`cell.color`) sempre vence a derivada.
 *
 * O match é exato sobre a string normalizada, não regex, porque o produto já
 * tem "Médio Completo"/"Médio Incompleto" (escolaridade) e "Média Normativa"
 * (cabeçalho de coluna) — qualquer expressão em "Médi" pintaria os três.
 *
 * Espelhado em `api/src/modules/reports/classificationColors.ts`, que é quem
 * pinta o DOCX: o backend renderiza a tabela a partir do JSON do bloco e a cor
 * não está gravada na célula. As duas listas mudam juntas.
 */
const COLOR_BY_LABEL: Record<string, TableCellColor> = {
    'muito inferior': 'red',
    inferior: 'red',
    'media inferior': 'red',
    'medio inferior': 'red',
    limitrofe: 'red',
    rebaixado: 'red',

    // "Média" e "Médio" ficam na tinta padrão: são o centro da escala, e uma
    // cor ali só acrescentaria ruído.

    'media superior': 'green',
    'medio superior': 'green',
    superior: 'green',
    'muito superior': 'green',
};

/** Minúsculas, sem acento, hífen vira espaço, espaços colapsados. */
const normalize = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[-\u2013\u2014]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

/** A cor do rótulo, ou `undefined` quando ele não está no dicionário. */
export const resolveClassificationColor = (value: string): TableCellColor | undefined =>
    COLOR_BY_LABEL[normalize(value)];

/**
 * Ligado por padrão: o laudo que nunca tocou no ajuste não tem o campo, e laudos
 * salvos antes da feature também não. Só um `false` explícito desliga.
 */
export const isClassificationColoringOn = (table?: TableCustomizations): boolean =>
    table?.color_classifications !== false;
